import { createApi } from '@reduxjs/toolkit/query/react';

import {
  getBaseQuery,
  getListProvidesTags,
  TransformedErrorResponseWithNotification,
  TransformedResponseWithNotification,
} from '../generic';
import * as endpoints from '../endpoints';
import { REDUX_CACHE, HTTP_METHOD, LANGUAGES } from '../constants';
import { ILanguage } from '../../../types/interfaces/language';
import { IAppFile } from '../../../types/interfaces/app-file';

export const languagesApi = createApi({
  reducerPath: 'languagesApi',
  tagTypes: [
    LANGUAGES.ACTIVE,
    LANGUAGES.ALL,
    REDUX_CACHE.ABORT_INVALIDATION,
    REDUX_CACHE.LIST,
  ],
  baseQuery: getBaseQuery(),
  // global configuration for the api
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getActiveLanguagesApi: builder.query<ILanguage[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.Language.ACTIVE,
          method: HTTP_METHOD.GET,
          headers: {},
        };
      },
      providesTags: (result) => getListProvidesTags(result, LANGUAGES.ACTIVE),
      ...TransformedErrorResponseWithNotification(),
    }),
    getLanguagesApi: builder.query<ILanguage[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.Language.ROOT,
          method: HTTP_METHOD.GET,
          headers: {},
        };
      },
      providesTags: (result) => getListProvidesTags(result, LANGUAGES.ALL),
      ...TransformedErrorResponseWithNotification(),
    }),
    postPutLanguage: builder.mutation<
      void,
      { id: string; body: ILanguage & { avatar?: IAppFile } }
    >({
      query({ id, body }) {
        return {
          url: id
            ? `${endpoints.Language.ROOT}/${id}`
            : endpoints.Language.ROOT,
          method: id ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: LANGUAGES.ALL,
              id: REDUX_CACHE.LIST,
            },
            {
              type: LANGUAGES.ACTIVE,
              id: REDUX_CACHE.LIST,
            },
            {
              type: LANGUAGES.ALL,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Language'),
    }),
    deleteLanguage: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: `${endpoints.Language.ROOT}/${id}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: LANGUAGES.ALL,
              id: params.id,
            },
            {
              type: LANGUAGES.ACTIVE,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Language'),
    }),
  }),
});

export const {
  useGetActiveLanguagesApiQuery: useGetActiveLanguages,
  useLazyGetActiveLanguagesApiQuery: useLazyGetActiveLanguages,
  useGetLanguagesApiQuery: useGetLanguages,
  useLazyGetLanguagesApiQuery: useLazyGetLanguages,
  usePostPutLanguageMutation: usePostPutLanguage,
  useDeleteLanguageMutation: useDeleteLanguage,
} = languagesApi;
